import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useConfig } from '../../../config';
import { useTranslation } from '../../../i18n/i18n';
import { useApi, useCacheInvalidator } from '../../../state/api';
import { Res } from '../../../state/api-resources';
import { ApiOrderItem, ApiOrderReturn } from '../../../types';
import { ContentCard } from '../../ContentCard/ContentCard';
import { Button } from '../../Form/Button';
import { QuantityInput } from '../../Form/QuantityInput';
import { RadioButton } from '../../Form/RadioButton';
import { TextArea } from '../../Form/TextArea';
import { Icon, close } from '../../Icon/Icon';
import { Img } from '../../Img/Img';
import { Modal } from '../../Modal/Modal';
import './ReturnProductModal.css';

interface ReturnProductModalProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	onClose: () => void;
	orderItems: ApiOrderItem[];
	orderId: string;
}

type ErrandType = 'reclamation' | 'return' | null;
type ProductCondition = 'unbroken' | 'opened_unused' | 'used' | null;
type ProductConditionOption = Exclude<ProductCondition, null>;

interface SelectedProduct {
	orderItem: ApiOrderItem;
	returnQuantity: number;
	productCondition: ProductCondition;
}

interface UploadedImage {
	id: string;
	file: File;
	status: 'uploading' | 'uploaded' | 'error';
	url?: string;
	error?: string;
	uploadedId?: string; // Cloudflare image id
}

interface ReclamationData {
	reason: ReclamationReason;
	description: string;
	images: UploadedImage[];
}

type ReturnReason = 'wrong_size' | 'regretted_purchase' | 'no_longer_needed' | 'not_as_expected' | 'other' | null;

type ReclamationReason =
	| 'broken_delivery'
	| 'color_faded'
	| 'quality_problem'
	| 'material_broken'
	| 'wrong_description'
	| 'wrong_product'
	| 'other'
	| null;

export const ReturnProductModal = ({
	open,
	setOpen,
	onClose,
	orderItems,
	orderId,
}: ReturnProductModalProps): JSX.Element => {
	const t = useTranslation();
	const [currentStep, setCurrentStep] = useState(0);
	const [selectedErrandType, setSelectedErrandType] = useState<ErrandType>(null);
	const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
	const [returnReason, setReturnReason] = useState<ReturnReason>(null);
	const [returnComment, setReturnComment] = useState('');
	const [reclamationData, setReclamationData] = useState<ReclamationData>({
		reason: null,
		description: '',
		images: [],
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { api } = useConfig();
	const { data: orderReturns } = useApi(Res.orderReturns, { orderId });
	const mutate = useCacheInvalidator();

	const handleReclamationDataChange = (
		dataOrUpdater: ReclamationData | ((prevData: ReclamationData) => ReclamationData)
	) => {
		if (typeof dataOrUpdater === 'function') {
			setReclamationData(dataOrUpdater);
		} else {
			setReclamationData(dataOrUpdater);
		}
	};

	const closeModal = () => {
		setOpen(false);
		onClose();
		// reset state when closing
		setCurrentStep(0);
		setSelectedErrandType(null);
		setSelectedProducts([]);
		setReturnReason(null);
		setReturnComment('');
		setReclamationData({
			reason: null,
			description: '',
			images: [],
		});
		setError(null);
		setIsSubmitting(false);
	};

	const handleNext = () => {
		if (currentStep < 4) {
			setCurrentStep(currentStep + 1);
			setError(null);
		}
	};

	const handlePrevious = () => {
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1);
		}
	};

	const handleSubmit = async () => {
		if (isSubmitting) return;
		if (!orderId) {
			setError('Ogiltigt order ID. Kontakta support.');
			return;
		}
		setIsSubmitting(true);
		setError(null);

		try {
			const uploadedImageIds = reclamationData.images
				.filter((image) => image.status === 'uploaded' && image.uploadedId)
				.map((image) => image.uploadedId!);

			let payload: any;
			if (selectedErrandType === 'reclamation') {
				payload = {
					reason: reclamationData.reason,
					description: reclamationData.description,
					images: uploadedImageIds,
					selectedProducts: selectedProducts.map((sp) => ({
						productId: sp.orderItem.product.id,
						returnQuantity: sp.returnQuantity,
						totalQuantity: sp.orderItem.quantity,
					})),
					type: selectedErrandType,
				};
			} else {
				payload = {
					reason: returnReason,
					selectedProducts: selectedProducts.map((sp) => ({
						productId: sp.orderItem.product.id,
						returnQuantity: sp.returnQuantity,
						totalQuantity: sp.orderItem.quantity,
						productCondition: sp.productCondition,
					})),
					type: selectedErrandType,
					description: returnComment,
				};
			}

			const response = await axios.post(api + Res.orderReturns.uri({ orderId }), payload, {
				withCredentials: true,
			});

			if (response.data.success) {
				// proceed to success step instead of closing modal
				setCurrentStep(4);
				mutate(Res.orderReturns, { orderId });
			} else {
				throw new Error('Request failed');
			}
		} catch (err) {
			console.error('Return order submission failed:', err);

			let errorMessage =
				'An error occurred while submitting your request. Please contact support if the problem persists.';

			if (err instanceof Error) {
				errorMessage = err.message;
			} else if (typeof err === 'string') {
				errorMessage = err;
			} else if (err && typeof err === 'object') {
				// axios errors
				if ('response' in err) {
					const axiosError = err as any;
					if (axiosError.response) {
						errorMessage = `Server error: ${axiosError.response.status} - ${axiosError.response.statusText}`;
					} else if (axiosError.request) {
						errorMessage =
							'Network error: Unable to connect to the server. Please check your internet connection and try again.';
					} else {
						errorMessage = axiosError.message || 'Request failed';
					}
				} else if ('message' in err) {
					errorMessage = (err as any).message;
				}
			}

			setError(errorMessage);
		} finally {
			setIsSubmitting(false);
		}
	};

	const getStepTitle = (): string => {
		switch (currentStep) {
			case 0:
				return t.returnOrder.selectErrandType;
			case 1:
				return t.returnOrder.selectProducts;
			case 2:
				return selectedErrandType === 'reclamation'
					? t.returnOrder.describeReclamation
					: t.returnOrder.reasonForReturn;
			case 3:
				return t.returnOrder.confirmRequest;
			case 4:
				return selectedErrandType === 'return'
					? t.returnOrder.returnRequestSubmitted
					: t.returnOrder.reclaimRequestSubmitted;
			default:
				return 'Return Product';
		}
	};

	const canProceedToNext = (): boolean => {
		switch (currentStep) {
			case 0:
				return selectedErrandType !== null;
			case 1:
				// Check if there are any products available for return
				if (!orderReturns || !orderReturns.length) {
					if (selectedProducts.length === 0) {
						return false;
					}

					if (selectedErrandType === 'return') {
						return selectedProducts.every((sp) => sp.productCondition !== null);
					}

					return true;
				}

				// Calculate available products
				const returnedQuantities = new Map<number, number>();
				orderReturns.forEach((orderReturn) => {
					orderReturn.items.forEach((item) => {
						const productId = item.product.id;
						const currentReturned = returnedQuantities.get(productId) || 0;
						returnedQuantities.set(productId, currentReturned + item.returnQuantity);
					});
				});
				const availableProducts = orderItems.filter((orderItem) => {
					const productId = orderItem.product.id;
					const originalQuantity = orderItem.quantity;
					const returnedQuantity = returnedQuantities.get(productId) || 0;
					const remainingQuantity = Math.max(0, originalQuantity - returnedQuantity);
					return remainingQuantity > 0;
				});

				// If no products available, disable next button
				if (availableProducts.length === 0) {
					return false;
				}

				if (selectedProducts.length === 0) {
					return false;
				}

				if (selectedErrandType === 'return') {
					return selectedProducts.every((sp) => sp.productCondition !== null);
				}

				return true;
			case 2:
				if (selectedErrandType === 'return') {
					return returnReason !== null;
				}
				if (selectedErrandType === 'reclamation') {
					return reclamationData.reason !== null && reclamationData.description.trim() !== '';
				}
				return true;
			case 3:
				return true; // always allow submission on confirmation step
			case 4:
				return true; // always allow proceeding to next on success step
			default:
				return false;
		}
	};

	const renderCurrentView = () => {
		switch (currentStep) {
			case 0:
				return <ErrandTypeSelection selectedType={selectedErrandType} onTypeSelect={setSelectedErrandType} />;
			case 1:
				return (
					<ProductSelection
						orderItems={orderItems}
						selectedProducts={selectedProducts}
						onProductsChange={setSelectedProducts}
						errandType={selectedErrandType}
						orderReturns={orderReturns}
					/>
				);
			case 2:
				return (
					<ErrandForm
						type={selectedErrandType}
						returnReason={returnReason}
						onReturnReasonChange={setReturnReason}
						returnComment={returnComment}
						onReturnCommentChange={setReturnComment}
						reclamationData={reclamationData}
						onReclamationDataChange={handleReclamationDataChange}
					/>
				);
			case 3:
				return (
					<Confirmation
						errandType={selectedErrandType}
						selectedProducts={selectedProducts}
						returnReason={returnReason}
						returnComment={returnComment}
						reclamationData={reclamationData}
					/>
				);
			case 4:
				return <SuccessMessage errandType={selectedErrandType} error={error} />;
			default:
				return <div>Invalid step</div>;
		}
	};

	return (
		<Modal
			className="return-product-modal"
			show={open}
			disableBodyScroll
			onDismiss={closeModal}
			toggle={() => setOpen(!open)}
		>
			<ContentCard className="return-product-content">
				<div className="modal-header">
					<div className="modal-header-title">{getStepTitle()}</div>
					<button className="close-btn" onClick={closeModal}>
						<Icon shape={close} size={20} />
					</button>
				</div>
				<div className="modal-body">{renderCurrentView()}</div>
				<div className="modal-footer">
					<div className="navigation-buttons">
						{currentStep > 0 && currentStep < 4 ? (
							<Button onClick={handlePrevious}>{t.returnOrder.back}</Button>
						) : (
							<div></div>
						)}
						{currentStep < 3 ? (
							<Button onClick={handleNext} disabled={!canProceedToNext()}>
								{t.returnOrder.next}
							</Button>
						) : currentStep === 3 ? (
							<Button onClick={handleSubmit} disabled={!canProceedToNext() || isSubmitting}>
								{isSubmitting ? t.returnOrder.creatingCase : t.returnOrder.sendRequest}
							</Button>
						) : (
							<Button onClick={closeModal}>{t.returnOrder.close}</Button>
						)}
					</div>
				</div>
			</ContentCard>
		</Modal>
	);
};

// step 0: errand selection
const ErrandTypeSelection = ({
	selectedType,
	onTypeSelect,
}: {
	selectedType: ErrandType;
	onTypeSelect: (type: ErrandType) => void;
}): JSX.Element => {
	const t = useTranslation();

	return (
		<div className="errand-type-selection">
			<h3 style={{ marginBottom: '3rem' }}>{t.returnOrder.whatDoYouWantToDo}</h3>
			<div className="radio-group">
				<RadioButton
					group="errandType"
					value="return"
					checked={selectedType === 'return'}
					onChange={(e) => onTypeSelect(e.target.value as ErrandType)}
				>
					{t.returnOrder.returnExpanded}
				</RadioButton>
				<RadioButton
					group="errandType"
					value="reclamation"
					checked={selectedType === 'reclamation'}
					onChange={(e) => onTypeSelect(e.target.value as ErrandType)}
				>
					{t.returnOrder.reclamationExpanded}
				</RadioButton>
			</div>
		</div>
	);
};

// step 1: product selection
const ProductSelection = ({
	orderItems,
	selectedProducts,
	onProductsChange,
	errandType,
	orderReturns,
}: {
	orderItems: ApiOrderItem[];
	selectedProducts: SelectedProduct[];
	onProductsChange: (products: SelectedProduct[]) => void;
	errandType: ErrandType;
	orderReturns?: ApiOrderReturn[];
}): JSX.Element => {
	const t = useTranslation();

	const productConditionOptions = [
		{ value: 'unbroken' as ProductConditionOption, label: t.returnOrder.conditionUnbroken },
		{ value: 'opened_unused' as ProductConditionOption, label: t.returnOrder.conditionOpenedUnused },
		{ value: 'used' as ProductConditionOption, label: t.returnOrder.conditionUsed },
	];

	const updateQuantity = (orderItemId: number, newQuantity: number) => {
		const orderItem = orderItems.find((oi) => oi.id === orderItemId);

		if (orderItem && newQuantity > orderItem.quantity) {
			return;
		}

		const selectedProduct = getSelectedProduct(orderItemId);

		if (orderItem?.quantity && selectedProduct && newQuantity > 0 && newQuantity <= orderItem.quantity) {
			onProductsChange(
				selectedProducts.map((sp) =>
					sp.orderItem.id === orderItemId ? { ...sp, returnQuantity: newQuantity } : sp
				)
			);
			return;
		}

		if (selectedProduct === undefined && newQuantity > 0) {
			if (orderItem) {
				onProductsChange([
					...selectedProducts,
					{ orderItem, returnQuantity: newQuantity, productCondition: null },
				]);
				return;
			}
		}
		if (newQuantity === 0) {
			onProductsChange(selectedProducts.filter((sp) => sp.orderItem.id !== orderItemId));
			return;
		}
	};

	const getSelectedProduct = (orderItemId: number): SelectedProduct | undefined => {
		return selectedProducts.find((sp) => sp.orderItem.id === orderItemId);
	};

	const updateCondition = (orderItemId: number, productCondition: ProductCondition) => {
		onProductsChange(
			selectedProducts.map((sp) => (sp.orderItem.id === orderItemId ? { ...sp, productCondition } : sp))
		);
	};

	const setConditionForAllSelected = (productCondition: ProductCondition) => {
		onProductsChange(selectedProducts.map((sp) => ({ ...sp, productCondition })));
	};

	const isConditionSelectedForAll = (productCondition: ProductConditionOption): boolean => {
		if (selectedProducts.length === 0) {
			return false;
		}

		return selectedProducts.every((sp) => sp.productCondition === productCondition);
	};

	// Calculate how many quantities of each product have already been returned
	const getReturnedQuantities = () => {
		if (!orderReturns) return new Map<number, number>();

		const returnedQuantities = new Map<number, number>();

		orderReturns.forEach((orderReturn) => {
			orderReturn.items.forEach((item) => {
				const productId = item.product.id;
				const currentReturned = returnedQuantities.get(productId) || 0;
				returnedQuantities.set(productId, currentReturned + item.returnQuantity);
			});
		});

		return returnedQuantities;
	};

	// Get remaining quantities for each product (original quantity - returned quantity)
	const getRemainingQuantities = () => {
		const returnedQuantities = getReturnedQuantities();
		const remainingQuantities = new Map<number, number>();

		orderItems.forEach((orderItem) => {
			const productId = orderItem.product.id;
			const originalQuantity = orderItem.quantity;
			const returnedQuantity = returnedQuantities.get(productId) || 0;
			const remainingQuantity = Math.max(0, originalQuantity - returnedQuantity);
			remainingQuantities.set(productId, remainingQuantity);
		});

		return remainingQuantities;
	};

	// Filter out products that have been fully returned
	const availableProducts = orderItems.filter((orderItem) => {
		const remainingQuantity = getRemainingQuantities().get(orderItem.product.id) || 0;
		return remainingQuantity > 0;
	});

	return (
		<div className="product-selection">
			<h3>
				{errandType === 'reclamation'
					? t.returnOrder.selectProductsToReclaim
					: t.returnOrder.selectProductsToReturn}
			</h3>
			{availableProducts.length === 0 ? (
				<div className="no-products-message">
					<p>{t.returnOrder.noProductsAvailableForReturn}</p>
				</div>
			) : (
				<>
					{errandType === 'return' && selectedProducts.length > 1 && (
						<div className="return-product-condition-all">
							<div className="condition-group-label">{t.returnOrder.selectStateForAllProducts}</div>
							<div className="condition-radio-group">
								{productConditionOptions.map((option) => (
									<RadioButton
										key={`all-${option.value}`}
										group="returnConditionAll"
										value={option.value}
										checked={isConditionSelectedForAll(option.value)}
										onChange={(e) =>
											setConditionForAllSelected(e.target.value as ProductConditionOption)
										}
									>
										{option.label}
									</RadioButton>
								))}
							</div>
						</div>
					)}
					<div className="product-list">
						{availableProducts.map((orderItem) => {
							const selectedProduct = getSelectedProduct(orderItem.id);
							const remainingQuantity = getRemainingQuantities().get(orderItem.product.id) || 0;

							return (
								<div key={orderItem.id} className="product-option-container">
									<OrderItem
										selectedProduct={selectedProduct}
										remainingQuantity={remainingQuantity}
										orderItem={orderItem}
										updateQuantity={updateQuantity}
										updateCondition={updateCondition}
										errandType={errandType}
										productConditionOptions={productConditionOptions}
									/>
								</div>
							);
						})}
					</div>
				</>
			)}
		</div>
	);
};

const OrderItem = ({
	orderItem,
	selectedProduct,
	remainingQuantity,
	updateQuantity,
	updateCondition,
	errandType,
	productConditionOptions,
}: {
	orderItem: ApiOrderItem;
	selectedProduct: SelectedProduct | undefined;
	remainingQuantity: number;
	updateQuantity: (orderItemId: number, newQuantity: number) => void;
	updateCondition: (orderItemId: number, productCondition: ProductCondition) => void;
	errandType: ErrandType;
	productConditionOptions: Array<{ value: ProductConditionOption; label: string }>;
}): JSX.Element => {
	const imgRef = useRef<HTMLImageElement>(null);

	useEffect(() => {
		updateQuantity(orderItem.id, 0);
	}, [orderItem]);

	const { quantity, product } = orderItem;

	const t = {
		...useTranslation().product,
		...useTranslation().orderPages,
		...useTranslation().returnOrder,
	};

	return (
		<div className="row">
			<div className="product-image">
				<Img ref={imgRef} src={orderItem.image} width={50} height={50} alt={product.name} />
			</div>
			<div className="product-details-wrapper">
				<div className="product-link">
					<Link to={'/-p-' + product.id}>{product.name}</Link>
				</div>
				<div className="article-number">
					<dl>
						<dt>{t.articleNumber}:</dt>
						<dd>{product.displayId}</dd>
					</dl>
				</div>
				<div className="quantity">
					<dl>
						<dt>{t.amount}:</dt>
						<dd>{quantity}</dd>
					</dl>
				</div>

				<div className="quantity-selector">
					<dl>
						<dt>{errandType === 'return' ? t.quantityToReturn : t.quantityToReclaim}:</dt>
						<dd>
							<QuantityInput
								allowZero
								minValue={0}
								defaultValue={selectedProduct?.returnQuantity || 0}
								maxValue={remainingQuantity}
								onBlur={(quantity) => updateQuantity(orderItem.id, quantity)}
							/>
						</dd>
					</dl>
				</div>
			</div>
			{errandType === 'return' && selectedProduct && selectedProduct.returnQuantity > 0 && (
				<div className="product-condition-selector">
					<dl>
						<dt>{t.isPackageUnopened}</dt>
						<dd>
							<div className="condition-radio-group">
								{productConditionOptions.map((option) => (
									<RadioButton
										key={`${orderItem.id}-${option.value}`}
										group={`returnCondition-${orderItem.id}`}
										value={option.value}
										checked={selectedProduct.productCondition === option.value}
										onChange={(e) =>
											updateCondition(orderItem.id, e.target.value as ProductConditionOption)
										}
									>
										{option.label}
									</RadioButton>
								))}
							</div>
						</dd>
					</dl>
				</div>
			)}
		</div>
	);
};

// step 2: errand Form
const ErrandForm = ({
	type,
	returnReason,
	onReturnReasonChange,
	returnComment,
	onReturnCommentChange,
	reclamationData,
	onReclamationDataChange,
}: {
	type: ErrandType;
	returnReason: ReturnReason;
	onReturnReasonChange: (reason: ReturnReason) => void;
	returnComment: string;
	onReturnCommentChange: (comment: string) => void;
	reclamationData: ReclamationData;
	onReclamationDataChange: (data: ReclamationData | ((prevData: ReclamationData) => ReclamationData)) => void;
}): JSX.Element => {
	const t = useTranslation();

	if (type === 'return') {
		return (
			<div className="errand-form">
				<h3>{t.returnOrder.whyReturnProducts}</h3>
				<div className="return-reasons-grid">
					<div className="reasons-column">
						<RadioButton
							group="returnReason"
							value="wrong_size"
							checked={returnReason === 'wrong_size'}
							onChange={(e) => onReturnReasonChange(e.target.value as ReturnReason)}
						>
							{t.returnOrder.wrongSize}
						</RadioButton>
						<RadioButton
							group="returnReason"
							value="regretted_purchase"
							checked={returnReason === 'regretted_purchase'}
							onChange={(e) => onReturnReasonChange(e.target.value as ReturnReason)}
						>
							{t.returnOrder.regrettedPurchase}
						</RadioButton>
						<RadioButton
							group="returnReason"
							value="no_longer_needed"
							checked={returnReason === 'no_longer_needed'}
							onChange={(e) => onReturnReasonChange(e.target.value as ReturnReason)}
						>
							{t.returnOrder.noLongerNeeded}
						</RadioButton>
					</div>
					<div className="reasons-column">
						<RadioButton
							group="returnReason"
							value="not_as_expected"
							checked={returnReason === 'not_as_expected'}
							onChange={(e) => onReturnReasonChange(e.target.value as ReturnReason)}
						>
							{t.returnOrder.notAsExpected}
						</RadioButton>
						<RadioButton
							group="returnReason"
							value="other"
							checked={returnReason === 'other'}
							onChange={(e) => onReturnReasonChange(e.target.value as ReturnReason)}
						>
							{t.returnOrder.other}
						</RadioButton>
					</div>
				</div>
				<div className="form-section">
					<h4>{t.returnOrder.returnCommentOptional}</h4>
					<TextArea
						placeholder={t.returnOrder.returnCommentPlaceholder}
						value={returnComment}
						onChange={(e) => onReturnCommentChange(e.target.value)}
						className="reclamation-description"
					/>
				</div>
			</div>
		);
	}

	if (type === 'reclamation') {
		return (
			<div className="errand-form">
				<h3>{t.returnOrder.describeWhatIsWrong}</h3>

				<div className="reclamation-form-sections">
					<div className="form-section">
						<h4>{t.returnOrder.reason}</h4>
						<div className="reclamation-reasons-grid">
							<div className="reasons-column">
								<RadioButton
									group="reclamationReason"
									value="broken_delivery"
									checked={reclamationData.reason === 'broken_delivery'}
									onChange={(e) =>
										onReclamationDataChange({
											...reclamationData,
											reason: e.target.value as ReclamationReason,
										})
									}
								>
									{t.returnOrder.brokenDelivery}
								</RadioButton>
								<RadioButton
									group="reclamationReason"
									value="color_faded"
									checked={reclamationData.reason === 'color_faded'}
									onChange={(e) =>
										onReclamationDataChange({
											...reclamationData,
											reason: e.target.value as ReclamationReason,
										})
									}
								>
									{t.returnOrder.colorFaded}
								</RadioButton>
								<RadioButton
									group="reclamationReason"
									value="quality_problem"
									checked={reclamationData.reason === 'quality_problem'}
									onChange={(e) =>
										onReclamationDataChange({
											...reclamationData,
											reason: e.target.value as ReclamationReason,
										})
									}
								>
									{t.returnOrder.qualityProblem}
								</RadioButton>
								<RadioButton
									group="reclamationReason"
									value="wrong_product"
									checked={reclamationData.reason === 'wrong_product'}
									onChange={(e) =>
										onReclamationDataChange({
											...reclamationData,
											reason: e.target.value as ReclamationReason,
										})
									}
								>
									{t.returnOrder.wrongProduct}
								</RadioButton>
							</div>
							<div className="reasons-column">
								<RadioButton
									group="reclamationReason"
									value="material_broken"
									checked={reclamationData.reason === 'material_broken'}
									onChange={(e) =>
										onReclamationDataChange({
											...reclamationData,
											reason: e.target.value as ReclamationReason,
										})
									}
								>
									{t.returnOrder.materialBroken}
								</RadioButton>
								<RadioButton
									group="reclamationReason"
									value="wrong_description"
									checked={reclamationData.reason === 'wrong_description'}
									onChange={(e) =>
										onReclamationDataChange({
											...reclamationData,
											reason: e.target.value as ReclamationReason,
										})
									}
								>
									{t.returnOrder.wrongDescription}
								</RadioButton>
								<RadioButton
									group="reclamationReason"
									value="other"
									checked={reclamationData.reason === 'other'}
									onChange={(e) =>
										onReclamationDataChange({
											...reclamationData,
											reason: e.target.value as ReclamationReason,
										})
									}
								>
									{t.returnOrder.other}
								</RadioButton>
							</div>
						</div>
					</div>

					<div className="form-section">
						<h4>{t.returnOrder.detailedDescription}</h4>
						<TextArea
							placeholder={t.returnOrder.describeBrokenDefective}
							value={reclamationData.description}
							onChange={(e) =>
								onReclamationDataChange({
									...reclamationData,
									description: e.target.value,
								})
							}
							className="reclamation-description"
						/>
					</div>

					<div className="form-section">
						<h4>{t.returnOrder.imagesRequired}</h4>
						<ImageUpload
							images={reclamationData.images}
							onImagesChange={(imagesOrUpdater) => {
								onReclamationDataChange((prevData) => {
									const newImages =
										typeof imagesOrUpdater === 'function'
											? imagesOrUpdater(prevData.images)
											: imagesOrUpdater;
									return {
										...prevData,
										images: newImages,
									};
								});
							}}
						/>
					</div>
				</div>
			</div>
		);
	}

	return <div>Invalid form type</div>;
};

const ImageUpload = ({
	images,
	onImagesChange,
}: {
	images: UploadedImage[];
	onImagesChange: (images: UploadedImage[] | ((prevImages: UploadedImage[]) => UploadedImage[])) => void;
}) => {
	const t = useTranslation();
	const config = useConfig();

	const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(event.target.files || []);
		const validFiles = files.filter(
			(file) => file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png'
		);

		if (images.length + validFiles.length <= 5) {
			const newImages = validFiles.map((file) => ({
				id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
				file,
				status: 'uploading' as const,
			}));
			onImagesChange([...images, ...newImages]);

			newImages.forEach((image) => uploadImage(image));
		}
	};

	const removeImage = (index: number) => {
		onImagesChange(images.filter((_, i) => i !== index));
	};

	const uploadImage = async (image: UploadedImage) => {
		try {
			const formData = new FormData();
			formData.append('file', image.file);

			const response = await fetch(config.api + 'me/images.json', {
				method: 'POST',
				body: formData,
				credentials: 'include',
			});

			if (response.ok) {
				const result = await response.json();
				onImagesChange((prevImages: UploadedImage[]) => {
					const updatedImages = prevImages.map((img: UploadedImage) =>
						img.id === image.id
							? {
									...img,
									status: 'uploaded' as const,
									url: result.url || result.image_url,
									uploadedId: result.imageId,
								}
							: img
					);
					return updatedImages;
				});
			} else {
				throw new Error(`Upload failed with status: ${response.status}`);
			}
		} catch (error) {
			onImagesChange((prevImages: UploadedImage[]) =>
				prevImages.map((img: UploadedImage) =>
					img.id === image.id
						? {
								...img,
								status: 'error' as const,
								error: error instanceof Error ? error.message : 'Upload failed',
							}
						: img
				)
			);
		}
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		const files = Array.from(e.dataTransfer.files);
		const validFiles = files.filter(
			(file) => file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png'
		);

		if (images.length + validFiles.length <= 5) {
			const newImages = validFiles.map((file) => ({
				id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
				file,
				status: 'uploading' as const,
			}));
			onImagesChange([...images, ...newImages]);

			newImages.forEach((image) => uploadImage(image));
		}
	};

	return (
		<div className="image-upload-container">
			<div className="image-upload-area" onDragOver={handleDragOver} onDrop={handleDrop}>
				<input
					type="file"
					multiple
					accept="image/jpeg,image/jpg,image/png"
					onChange={handleFileSelect}
					className="file-input"
					id="image-upload"
				/>
				<label htmlFor="image-upload" className="upload-label">
					<div className="upload-icon">↑</div>
					<div className="upload-text">{t.returnOrder.clickToUpload}</div>
					<div className="upload-info">{t.returnOrder.maxImages}</div>
				</label>
			</div>

			{images.length > 0 && (
				<div className="uploaded-images">
					{images.map((image, index) => (
						<div key={image.id} className="uploaded-image">
							{image.status === 'uploading' && (
								<div className="uploading-indicator">
									<div className="uploading-spinner"></div>
									<span>Laddar upp bild...</span>
								</div>
							)}
							{image.status === 'uploaded' && (
								<div className="uploaded-image-container">
									<img
										src={image.url || URL.createObjectURL(image.file)}
										alt={`Uploaded ${index + 1}`}
										className="preview-image"
									/>
									<button
										type="button"
										onClick={() => removeImage(index)}
										className="remove-image-btn"
									>
										×
									</button>
								</div>
							)}
							{image.status === 'error' && (
								<div className="error-indicator">
									<span>{image.error || 'Uppladdning misslyckades'}</span>
									<button
										type="button"
										onClick={() => removeImage(index)}
										className="remove-image-btn"
									>
										×
									</button>
								</div>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

// step 3: confirmation
const Confirmation = ({
	errandType,
	selectedProducts,
	returnReason,
	returnComment,
	reclamationData,
}: {
	errandType: ErrandType;
	selectedProducts: SelectedProduct[];
	returnReason: ReturnReason;
	returnComment: string;
	reclamationData: ReclamationData;
}): JSX.Element => {
	const t = useTranslation();

	const getReturnReasonText = (reason: ReturnReason): string => {
		switch (reason) {
			case 'wrong_size':
				return t.returnOrder.wrongSize;
			case 'regretted_purchase':
				return t.returnOrder.regrettedPurchase;
			case 'no_longer_needed':
				return t.returnOrder.noLongerNeeded;
			case 'not_as_expected':
				return t.returnOrder.notAsExpected;
			case 'other':
				return t.returnOrder.other;
			default:
				return t.returnOrder.notSelected;
		}
	};

	const getReclamationReasonText = (reason: ReclamationReason): string => {
		switch (reason) {
			case 'broken_delivery':
				return t.returnOrder.brokenDelivery;
			case 'color_faded':
				return t.returnOrder.colorFaded;
			case 'quality_problem':
				return t.returnOrder.qualityProblem;
			case 'material_broken':
				return t.returnOrder.materialBroken;
			case 'wrong_description':
				return t.returnOrder.wrongDescription;
			case 'wrong_product':
				return t.returnOrder.wrongProduct;
			case 'other':
				return t.returnOrder.other;
			default:
				return t.returnOrder.notSelected;
		}
	};

	const getProductConditionText = (condition: ProductCondition): string => {
		switch (condition) {
			case 'unbroken':
				return t.returnOrder.conditionUnbroken;
			case 'opened_unused':
				return t.returnOrder.conditionOpenedUnused;
			case 'used':
				return t.returnOrder.conditionUsed;
			default:
				return t.returnOrder.notSelected;
		}
	};

	return (
		<div className="confirmation">
			<h3>{errandType === 'reclamation' ? t.returnOrder.reviewReclamation : t.returnOrder.reviewReturn}</h3>
			<div className="confirmation-details">
				{errandType === 'return' && (
					<>
						<p>
							<strong>{t.returnOrder.reason}:</strong> {getReturnReasonText(returnReason)}
						</p>
						<p>
							<strong>{t.returnOrder.returnCommentOptional}:</strong>{' '}
							{returnComment.trim() ? returnComment : t.returnOrder.notSelected}
						</p>
					</>
				)}
				{errandType === 'reclamation' && (
					<>
						<p>
							<strong>{t.returnOrder.reason}:</strong> {getReclamationReasonText(reclamationData.reason)}
						</p>
						<p>
							<strong>{t.returnOrder.description}:</strong> {reclamationData.description}
						</p>
						{reclamationData.images.length > 0 && (
							<div>
								<p>
									<strong>{t.returnOrder.images}:</strong>
								</p>
								<div className="confirmation-images">
									{reclamationData.images
										.filter((image) => image.status === 'uploaded')
										.map((image, index) => (
											<div key={image.id} className="confirmation-image">
												<img
													src={image.url || URL.createObjectURL(image.file)}
													alt={`Uploaded ${index + 1}`}
													className="confirmation-preview-image"
												/>
											</div>
										))}
								</div>
							</div>
						)}
					</>
				)}
				<p>
					<strong>{t.returnOrder.products}:</strong>
				</p>
				<ul className="selected-products-list">
					{selectedProducts.map((sp) => (
						<li key={sp.orderItem.id} className="selected-product-item">
							<div className="selected-product-image-wrapper">
								<img
									src={sp.orderItem.image}
									alt={sp.orderItem.product.name}
									className="selected-product-image"
								/>
							</div>
							<div>{sp.orderItem.product.name}</div>
							<div className="selected-product-quantity">
								<strong>{sp.returnQuantity}</strong> {t.returnOrder.of}{' '}
								<strong>{sp.orderItem.quantity}</strong>
							</div>
							{errandType === 'return' && (
								<div className="selected-product-condition">
									<strong>{t.returnOrder.productState}:</strong>{' '}
									{getProductConditionText(sp.productCondition)}
								</div>
							)}
						</li>
					))}
				</ul>
			</div>
		</div>
	);
};

// step 4: success message
const SuccessMessage = ({ error, errandType }: { error?: string | null; errandType: ErrandType }) => {
	const t = useTranslation();

	if (error) {
		return (
			<div className="success-message">
				<div className="error-message">
					<h3>Error</h3>
					<p>{error}</p>
					<p>Please contact support if the problem persists.</p>
				</div>
			</div>
		);
	}

	return (
		<div className="success-message">
			<p style={{ marginTop: 0 }}>
				{errandType === 'reclamation'
					? t.returnOrder.reclaimRequestSubmittedMessage
					: t.returnOrder.returnRequestSubmittedMessage}
			</p>
			<br />
		</div>
	);
};
